import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
   DEFAULT_BREAKDOWN_BY,
   DEFAULT_DATE_BASIS,
   DEFAULT_SECTION_TAB,
   DEFAULT_THRESHOLD_DIAS,
   SECTION_TABS,
   THRESHOLD_OPTIONS,
   UNCLASSIFIED_TOKEN,
} from '../constants';

// ---------------------------------------------------------------------------
// Estado do recorte — a URL é a fonte da verdade (navegacao.md §1.1).
//
// Leitura: UMA vez, no mount (getters do useState). Mudança posterior da URL
// não sobrescreve o que o operador está mexendo.
// Escrita: a cada mudança, com `replace: true` — trocar um chip NÃO empilha
// entrada no histórico, então o ◀ do navegador sai da tela em vez de desfazer
// filtro a filtro (navegacao.md §1.6).
//
// ⚠️ Desvio consciente da navegacao.md §1.2/§1.4 (busca explícita / URL limpa
// não busca): aquelas regras são de tela de LISTAGEM. Isto é um dashboard — o
// default é "Tudo", ele carrega no mount e reage na hora ao filtro. Um painel
// que abre vazio esperando "Pesquisar" não é painel. O que se preserva é o
// essencial: URL = fonte da verdade, `replace: true`, deep-link e F5
// restaurando o recorte exato.
//
// Só o que é RECORTE vai para a URL. `isolatedSafra` e `showQuality` são estado
// de leitura da tela, não recorte de dado — ficam fora.
// ---------------------------------------------------------------------------

// Lê o CSV de ids da URL. Além dos inteiros positivos, preserva o token `none`
// (handoff 0086 §2.2) — "registros sem classificação".
//
// ⚠️ Preservar o `none` é obrigatório, não conveniência: sem isto o chip "Sem
// classificação" acenderia na tela e o request sairia SEM o filtro, que é
// exatamente o filtro fantasma que o token veio resolver. Um bug nosso,
// silencioso, imitando o bug do backend que acabou de ser corrigido.
//
// O resto continua descartado: lixo na URL (colada, editada à mão) vira "sem
// filtro" em vez de 422. O 422 do backend existe para bug de cliente de
// verdade; a URL é superfície hostil e o descarte aqui é a defesa dela.
function parseIdCsv(value) {
   if (!value) return [];
   return value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) =>
         part === UNCLASSIFIED_TOKEN ? UNCLASSIFIED_TOKEN : Number(part),
      )
      .filter(
         (entry) =>
            entry === UNCLASSIFIED_TOKEN ||
            (Number.isInteger(entry) && entry > 0),
      );
}

function readInitialState(searchParams) {
   const rawThreshold = Number(searchParams.get('threshold_dias'));
   const rawBasis = searchParams.get('date_basis');
   const rawBy = searchParams.get('by');
   const rawTab = searchParams.get('tab');

   return {
      // Aba visível. Não é filtro — não vai para o backend e não refaz chamada
      // nenhuma. Vive na URL só para F5 e link compartilhado caírem na mesma
      // aba em que a pessoa estava.
      tab: SECTION_TABS.some((section) => section.key === rawTab)
         ? rawTab
         : DEFAULT_SECTION_TAB,
      from: searchParams.get('from') ?? '',
      to: searchParams.get('to') ?? '',
      // Valor fora do contrato na URL (digitado à mão) volta ao default em vez
      // de virar 422 no primeiro request.
      dateBasis:
         rawBasis === 'current' || rawBasis === 'original'
            ? rawBasis
            : DEFAULT_DATE_BASIS,
      originIds: parseIdCsv(searchParams.get('origin_id')),
      bankAccountIds: parseIdCsv(searchParams.get('bank_account_id')),
      by:
         rawBy === 'origin' || rawBy === 'bank_account' || rawBy === 'customer'
            ? rawBy
            : DEFAULT_BREAKDOWN_BY,
      thresholdDias: THRESHOLD_OPTIONS.includes(rawThreshold)
         ? rawThreshold
         : DEFAULT_THRESHOLD_DIAS,
   };
}

export default function useDashboardFilters() {
   const [searchParams, setSearchParams] = useSearchParams();

   // Leitura única no mount — a dependência de `searchParams` aqui seria um bug
   // (a escrita da própria tela voltaria como "mudança externa").
   // eslint-disable-next-line react-hooks/exhaustive-deps
   const [state, setState] = useState(() => readInitialState(searchParams));

   // Query string vigente, sem virar dependência de efeito (a leitura da URL é
   // só no mount; isto aqui é só para não reescrever o que já está lá).
   const currentQueryRef = useRef(searchParams.toString());
   currentQueryRef.current = searchParams.toString();

   // Espelha o estado na URL. Só os valores DIFERENTES do default entram: com o
   // recorte padrão a URL fica limpa e compartilhável.
   //
   // ⚠️ A guarda de igualdade não é cosmética: o `setSearchParams` do
   // react-router é recriado sempre que `location.search` muda, então ele é
   // dependência instável deste efeito. Sem comparar antes de escrever, cada
   // navegação realimentaria o efeito (e a URL ganharia um "?" pendurado no
   // recorte padrão, que serializa vazio).
   useEffect(() => {
      const params = {};
      if (state.from) params.from = state.from;
      if (state.to) params.to = state.to;
      if (state.dateBasis !== DEFAULT_DATE_BASIS) {
         params.date_basis = state.dateBasis;
      }
      if (state.originIds.length) params.origin_id = state.originIds.join(',');
      if (state.bankAccountIds.length) {
         params.bank_account_id = state.bankAccountIds.join(',');
      }
      if (state.tab !== DEFAULT_SECTION_TAB) params.tab = state.tab;
      if (state.by !== DEFAULT_BREAKDOWN_BY) params.by = state.by;
      if (state.thresholdDias !== DEFAULT_THRESHOLD_DIAS) {
         params.threshold_dias = String(state.thresholdDias);
      }

      const next = new URLSearchParams(params).toString();
      if (next === currentQueryRef.current) return;

      setSearchParams(params, { replace: true });
   }, [state, setSearchParams]);

   // ─── Recorte GLOBAL: mudar qualquer um destes refaz as 5 chamadas ───────
   const globalFilters = useMemo(
      () => ({
         from: state.from,
         to: state.to,
         dateBasis: state.dateBasis,
         originIds: state.originIds,
         bankAccountIds: state.bankAccountIds,
      }),
      [
         state.from,
         state.to,
         state.dateBasis,
         state.originIds,
         state.bankAccountIds,
      ],
   );

   const setDateRange = useCallback(({ from, to }) => {
      setState((prev) => ({
         ...prev,
         from: from ?? prev.from,
         to: to ?? prev.to,
      }));
   }, []);

   const setDateBasis = useCallback((dateBasis) => {
      setState((prev) => ({ ...prev, dateBasis }));
   }, []);

   // O dropdown de seleção múltipla devolve a lista inteira, não o item que
   // mudou — ele não guarda estado de seleção, quem manda é a URL.
   const setOrigins = useCallback((originIds) => {
      setState((prev) => ({ ...prev, originIds }));
   }, []);

   const setBankAccounts = useCallback((bankAccountIds) => {
      setState((prev) => ({ ...prev, bankAccountIds }));
   }, []);

   const setTab = useCallback((tab) => {
      setState((prev) => ({ ...prev, tab }));
   }, []);

   const setBy = useCallback((by) => {
      setState((prev) => ({ ...prev, by }));
   }, []);

   const setThresholdDias = useCallback((thresholdDias) => {
      setState((prev) => ({ ...prev, thresholdDias }));
   }, []);

   // "Limpar filtros" do empty state: volta ao recorte padrão inteiro (inclusive
   // a lente), o que devolve a base completa.
   const clearAll = useCallback(() => {
      setState((prev) => ({
         ...prev,
         from: '',
         to: '',
         dateBasis: DEFAULT_DATE_BASIS,
         originIds: [],
         bankAccountIds: [],
      }));
   }, []);

   return {
      globalFilters,
      tab: state.tab,
      by: state.by,
      thresholdDias: state.thresholdDias,
      setTab,
      setDateRange,
      setDateBasis,
      setOrigins,
      setBankAccounts,
      setBy,
      setThresholdDias,
      clearAll,
   };
}
